import { TIME_MS } from '@core-systems/frontend-shared';
import { queryOptions } from '@tanstack/react-query';
import { wmwAppsyncQueries } from 'lib-appsync';
import { ZoneData } from '../api/types';
import apiClient from '../utils/apiClient';

export const TRADING_ZONES_QUERY_KEY = ['tradingZones'] as const;

const fetchTradingZones = async (signal?: AbortSignal): Promise<Array<ZoneData>> => {
  const response = await (apiClient.graphql({
    query: wmwAppsyncQueries.tradingZones
  }) as Promise<{ data?: { tradingZones?: string | null } }>);

  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const payload = response?.data?.tradingZones;
  if (typeof payload !== 'string') {
    throw new Error('Invalid trading zones payload: expected JSON string');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    throw new Error('Invalid trading zones payload: expected valid JSON string');
  }

  if (!Array.isArray(parsed)) {
    throw new Error('Invalid trading zones payload: expected JSON array');
  }

  return parsed as Array<ZoneData>;
};

/**
 * Query options for fetching all trading zones.
 * TanStack Query handles abort via its own signal.
 *
 * Usage:
 * ```ts
 * const { data } = useQuery(tradingZonesQueryOptions);
 * ```
 */
export const tradingZonesQueryOptions = queryOptions({
  queryKey: TRADING_ZONES_QUERY_KEY,
  queryFn: ({ signal }) => fetchTradingZones(signal),
  staleTime: 5 * TIME_MS.MINUTE // Trading zones are static JSON; avoid refetching on every focus
});
